import React from 'react';
import PageHeader from 'components/general/pageHeader';
import { useSelector } from 'react-redux';
import { apiGet } from 'services';
import { translateCompetency, translateCourse, translatePI } from 'helpers/translations';
import { Copy, Help, Loading, SystemText } from 'e4shared';

import Bullet from 'components/prism/bullet';
import Course from 'components/prism/course';
import Rating from 'components/prism/rating';
import Resource from 'components/prism/resource';
import ObjectiveSummary from 'components/prism/objectiveSummary';
import { Link } from 'react-router-dom';
import BlobImage from 'components/blobImage';
import { Overtime } from './components/overtime';

export const Behaviour = ({ match }) => {
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState(null);
    const [completedObjectives, setCompletedObjectives] = React.useState([]);
    const [incompleteObjectives, setIncompleteObjectives] = React.useState([]);
    const [hasUserRating, setHasUserRating] = React.useState(false);
    const [hasManagerRating, setHasManagerRating] = React.useState(false);

    const { user } = useSelector(state => state.user);
    const { prismMultiColumn } = useSelector(state => state.ui);
    const ratingsExplaination = useSelector(
        state =>
            state.user.user?.companySettings?.customText?.ratingsExplaination || SystemText('Admin/Config/Defaults/Ratings')
    );

    React.useEffect(() => {
        const translateResponse = response => {
            response.behaviour = translateCompetency(user.language_Code, response.behaviour);
            response.positiveIndicators = response.positiveIndicators.map(pi => translatePI(user.language_Code, pi));
            response.courses = response.courses.map(c => translateCourse(user.language_Code, c));
            return response;
        };

        if (user?.id && match.params.id) {
            apiGet(`behaviour-details/${match.params.id}/${user.id}`, user.guid, response => {
                setData(translateResponse(response));
                setCompletedObjectives(response.objectives.filter(x => x.progress === 100));
                setIncompleteObjectives(response.objectives.filter(x => x.progress !== 100));
                setHasUserRating(response.positiveIndicators.some(pi => pi.userRated));
                setHasManagerRating(response.positiveIndicators.some(pi => pi.managerRated));
                setLoading(false);
            });
        }
    }, [user?.id, match.params.id]);

    if (loading) return <Loading />;

    const text = {
        chart: {
            title: SystemText('BehaviourDetails/Chart/Title'), //Behaviour rating over time
            rating: SystemText('BehaviourDetails/Chart/Rating'), //Behaviour rating
            outstanding: SystemText('BehaviourDetails/Chart/Outstanding'), //Outstanding Objectives
        },
        table: {
            title: SystemText('BehaviourDetails/Table/Title'), //How we measure this Behaviour
            positive: SystemText('BehaviourDetails/Table/Positive'), //Positive indicator
            your: SystemText('BehaviourDetails/Table/Your'), //Your Rating
            manager: SystemText('BehaviourDetails/Table/Manager'), //Manager Rating
        },
        relatedTo: SystemText('BehaviourDetails/RelatedTo'), //Related to
        courses: SystemText('BehaviourDetails/Courses'), //Courses
        additional: SystemText('BehaviourDetails/Additional'), //Additional Resources
        completed: SystemText('BehaviourDetails/Completed'), //Completed
        resume: SystemText('Welcome/Pathway/Resume'), //Resume
        start: SystemText('Welcome/Pathway/Start'), //Start
    };

    return (
        <div id="behaviour-page" data-testid="behaviour-page">
            <PageHeader metaTitle={data.behaviour.name} />
            <div className="prism-top-banner mb30 mtm10">
                {data.behaviour.image ? (
                    <BlobImage src={data.behaviour.image} folder="competency_Image" noholder />
                ) : (
                    <img src="/dist/img/defaults/behavior-default.jpg" />
                )}
            </div>
            <div className={prismMultiColumn ? 'two-col-66' : ''}>
                <div className="">
                    <div className="flex flex-center mb20">
                        <div className="flex-grow">
                            <Bullet type="behavior" />
                        </div>
                        <div className="btn-fake">
                            <i className={`fa fa-flag ${data.behaviour.flagged ? 'fa-solid' : ''}`} />
                        </div>
                    </div>
                    <h1 className="mb10">{data.behaviour.name}</h1>
                    {data.behaviour.description && <p className="mb10">{data.behaviour.description}</p>}
                    {data.positiveIndicators.length > 0 && (
                        <>
                            <h3 className="mb10">{text.table.title}</h3>
                            <table className="prism-data-table mb20">
                                <thead>
                                    <tr>
                                        <th>{text.table.positive}</th>
                                        {hasUserRating && (
                                            <th className="text-center">
                                                <Copy value={text.table.your} />
                                            </th>
                                        )}
                                        {hasManagerRating && (
                                            <th className="text-center">
                                                <Copy value={text.table.manager} />
                                            </th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.positiveIndicators.map(pi => {
                                        return (
                                            <tr key={`pi-${pi.id}`}>
                                                <td>{pi.statement}</td>
                                                {hasUserRating && (
                                                    <td className="text-center">
                                                        <Rating level={pi.userRated} hideOutOf />
                                                    </td>
                                                )}
                                                {hasManagerRating && (
                                                    <td className="text-center">
                                                        <Rating level={pi.managerRated} hideOutOf />
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    )}
                    {data.metrics.length > 0 && (
                        <>
                            <h3 className="mb10">{text.relatedTo}</h3>
                            <div className="bullet-list">
                                {data.metrics.map(metric => {
                                    return <Bullet key={`metric-${metric.id}`} value={metric.name} style="plum" />;
                                })}
                            </div>
                        </>
                    )}
                </div>
                <div className={`prism-content-box faded ${!prismMultiColumn ? 'mt20' : ''}`}>
                    <div className="prism-content">
                        <h6>{text.chart.title}</h6>
                        <div style={{ height: '200px', width: '100%' }}>
                            <Overtime data={data.overtime} />
                        </div>
                        <h6 className="mt20">
                            <span>{text.chart.rating}</span>
                            {ratingsExplaination && <Help id="behaviour-rating">{ratingsExplaination}</Help>}
                        </h6>
                        <div>
                            <Rating level={data.behaviour.level} />
                        </div>

                        {incompleteObjectives.length > 0 && (
                            <>
                                <h6 className="mt20">{text.chart.outstanding}</h6>
                                <div className="bullet-list flex flex-wrap">
                                    {incompleteObjectives.map(obj => {
                                        return (
                                            <Link key={`metric-${obj.id}`} to={`/objectives/${user.id}/objective/${obj.id}`}>
                                                <Bullet value={obj.name} style="info" />
                                            </Link>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            {data.courses.length > 0 && (
                <div className="mt30">
                    <div className="flex flex-center mb10">
                        <h2>
                            {text.courses}
                            <span className="ml10" style={{ fontWeight: 400 }}>
                                {data.courses.length}
                            </span>
                        </h2>
                    </div>
                    {data.courses.length === 1 ? (
                        <Link data-testid="pathway-item" to={data.courses[0].url} className="prism-content-box">
                            {prismMultiColumn && (
                                <div className="prism-banner">
                                    <img src="/dist/img/defaults/course-default.jpg" />
                                </div>
                            )}
                            <div className="prism-content">
                                <Bullet type="course" />
                                <div className="prism-progress">
                                    <div className="prism-progress-bar">
                                        <div style={{ width: `${data.courses[0].progress}%` }} />
                                    </div>
                                    <div>{data.courses[0].progress}%</div>
                                </div>
                                <h3>{data.courses[0].name}</h3>
                                {data.courses[0].description && <p className="truncate-4">{data.courses[0].description}</p>}
                                <button className="btn btn-primary">
                                    {data.courses[0].progress ? text.resume : text.start}
                                </button>
                            </div>
                        </Link>
                    ) : (
                        <div className={prismMultiColumn ? 'even-col' : 'one-col'}>
                            {data.courses.map(course => (
                                <Course key={`course-${course.id}`} course={course} padBottom={!prismMultiColumn} />
                            ))}
                        </div>
                    )}
                </div>
            )}
            {data.resources.length > 0 && (
                <div className="mt30">
                    <div className="flex flex-center mb10">
                        <h2>
                            {text.additional}
                            <span className="ml10" style={{ fontWeight: 400 }}>
                                {data.resources.length}
                            </span>
                        </h2>
                    </div>
                    <div className={prismMultiColumn ? 'even-col' : 'one-col'}>
                        {data.resources.map(resource => (
                            <Resource key={`resource-${resource.id}`} resource={resource} padBottom={!prismMultiColumn} />
                        ))}
                    </div>
                </div>
            )}
            {completedObjectives.length > 0 && (
                <div className="mt30">
                    <div className="flex flex-center mb10">
                        <h2>
                            {text.completed}
                            <span className="ml10" style={{ fontWeight: 400 }}>
                                {completedObjectives.length}
                            </span>
                        </h2>
                    </div>
                    <div className={prismMultiColumn ? 'even-col' : ''}>
                        {completedObjectives.map(objective => (
                            <ObjectiveSummary
                                key={`objective-${objective.id}`}
                                objective={objective}
                                padBottom={!prismMultiColumn}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Behaviour;
